import type {
  ProofClaimReason,
  ProofClaimStatus,
  ProofPanelClaim,
  ProofPanelFinding,
} from "./proofRun";

/**
 * A claim's or finding's status for the dashboard panel. It is fuller than the
 * PR comment's notes (ProofReport.php's claimNote and findingLine), which drop
 * reasons. `mark` is the verdict beside the label:
 * matching the claim, or differing on the run's head.
 */
export interface ProofClaimLabel {
  text: string;
  mark: "matches" | "differs" | null;
}

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

/** `blocked (environment)`, `not run` */
function resultWord(
  status: ProofClaimStatus,
  reason?: ProofClaimReason,
): string {
  const word = status.replaceAll("_", " ");
  return reason ? `${word} (${reason})` : word;
}

/** `blocked (environment)`, `not run`; null unless the claim closed without an observation. */
export function gapWord(item: {
  status: ProofClaimStatus;
  reason?: ProofClaimReason;
}): string | null {
  return item.status === "blocked" ||
    item.status === "skipped" ||
    item.status === "not_run"
    ? resultWord(item.status, item.reason)
    : null;
}

function atOlderHead(text: string, olderHead: string | undefined): string {
  return olderHead ? `${text} at ${olderHead}` : text;
}

function earlier(text: string, olderHead: string | undefined): string {
  return olderHead ? `${text} earlier (${olderHead})` : text;
}

/** ProofClaim.php's differsFromClaim: Proof saw the claim and it did not hold, on any head. */
export function claimDiffers(
  item: Pick<ProofPanelClaim, "status" | "matchesClaim">,
): boolean {
  return item.status === "observed" && item.matchesClaim === false;
}

/** ProofReport.php's passed: observed as claimed, on any head. */
export function claimPassed(
  item: Pick<ProofPanelClaim, "status" | "matchesClaim">,
): boolean {
  return item.status === "observed" && item.matchesClaim === true;
}

/** What a claim's mark draws: an observed claim is `matches`, `differs` or, unconfirmed, `observed`, any other claim its status. */
export type ClaimMarkState =
  | Exclude<ProofClaimStatus, "observed">
  | "matches"
  | "observed"
  | "differs";

function claimResult(
  item: Pick<
    ProofPanelClaim,
    "status" | "matchesClaim" | "evidence" | "reason"
  >,
): {
  word: string;
  mark: ClaimMarkState;
} {
  if (claimDiffers(item)) return { word: "Differs", mark: "differs" };
  if (item.status === "observed") {
    return claimPassed(item)
      ? { word: "Matches", mark: "matches" }
      : { word: "Observed", mark: "observed" };
  }
  return {
    word: capitalize(resultWord(item.status, item.reason)),
    mark: item.status,
  };
}

export function claimMarkState(
  item: Pick<
    ProofPanelClaim,
    "status" | "matchesClaim" | "evidence" | "reason"
  >,
): ClaimMarkState {
  return claimResult(item).mark;
}

/** `Matches`, `Differs`, `Blocked (environment)`: the result on whichever head the claim closed on. */
export function claimStatusWord(item: ProofPanelClaim): string {
  return claimResult(item).word;
}

/**
 * `Matches`, `Matches earlier (9f8e7d6c)`, `Differs`,
 * `Blocked (environment) earlier (9f8e7d6c)`. The detail view has no footer naming
 * the earlier commit, so the label carries it.
 */
export function proofClaimLabel(item: ProofPanelClaim): ProofClaimLabel {
  const { word, mark } = claimResult(item);
  const verdict = mark === "matches" || mark === "differs" ? mark : null;
  return {
    text: earlier(word, item.olderHead),
    mark:
      verdict === "differs" && item.olderHead !== undefined ? null : verdict,
  };
}

/** `Issue (unplanned)`; an issue on the run's head is a difference the comment calls out. */
export function proofFindingLabel(item: ProofPanelFinding): ProofClaimLabel {
  return {
    text: atOlderHead(
      `${capitalize(item.severity)} (unplanned)`,
      item.olderHead,
    ),
    mark:
      item.severity === "issue" && item.olderHead === undefined
        ? "differs"
        : null,
  };
}

const normalize = (text: string) => text.trim().toLowerCase();

/** What Proof saw, unless it only repeats the title. */
export const distinctObserved = (title: string, observed?: string) =>
  observed && normalize(observed) !== normalize(title) ? observed : undefined;

/** The claim, unless it repeats the title or what Proof saw. */
export function distinctClaim({
  title,
  observed,
  claim,
}: {
  title: string;
  observed?: string;
  claim?: string;
}): string | undefined {
  if (!claim) return undefined;
  const said = normalize(claim);
  const repeats = [title, observed].some((text) => {
    if (!text) return false;
    const other = normalize(text);
    return other.includes(said) || said.includes(other);
  });
  return repeats ? undefined : claim;
}
